<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <button
                  class="btn btn-outline-secondary btn-sm mb-0 me-3"
                  @click="$router.push('/products')"
                >
                  <i class="fas fa-arrow-left me-1"></i> 返回
                </button>
                <h6 class="mb-0">商品詳情</h6>
              </div>
              <div class="d-flex gap-2">
                <soft-button color="info" size="sm" @click="showQRModal = true">
                  <i class="fas fa-qrcode me-1"></i> QR Code
                </soft-button>
                <soft-button
                  color="success"
                  size="sm"
                  @click="$router.push(`/products/${product.id}/edit`)"
                >
                  <i class="fas fa-edit me-1"></i> 編輯
                </soft-button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <!-- Product Image -->
              <div class="col-md-4">
                <img
                  :src="product.image_url || '/no-image.png'"
                  class="img-fluid border-radius-lg"
                  alt="product"
                />
              </div>

              <!-- Product Info -->
              <div class="col-md-8">
                <h4 class="mb-3">{{ product.name }}</h4>

                <!-- Basic Info -->
                <div class="card mb-3">
                  <div class="card-body">
                    <h6 class="text-sm text-uppercase text-secondary mb-3">
                      基本資訊
                    </h6>
                    <div class="row">
                      <div class="col-md-6">
                        <p class="text-sm mb-2">
                          <strong>商品編號：</strong>{{ product.product_code }}
                        </p>
                        <p class="text-sm mb-2">
                          <strong>品牌：</strong
                          >{{ product.brand ? product.brand.name : "無" }}
                        </p>
                        <p class="text-sm mb-2">
                          <strong>型號：</strong>{{ product.model || "無" }}
                        </p>
                        <p class="text-sm mb-2">
                          <strong>年份：</strong>{{ product.year || "無" }}
                        </p>
                      </div>
                      <div class="col-md-6">
                        <p class="text-sm mb-2">
                          <strong>狀況：</strong
                          >{{ product.condition === "new" ? "全新" : "二手" }}
                        </p>
                        <p class="text-sm mb-2">
                          <strong>價格：</strong>NT$ {{ product.price }}
                        </p>
                        <p class="text-sm mb-2" v-if="product.suggested_price">
                          <strong>建議售價：</strong>NT$
                          {{ product.suggested_price }}
                        </p>
                        <p class="text-sm mb-2">
                          <strong>庫存：</strong>{{ product.stock }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Product Type -->
                <div class="card mb-3">
                  <div class="card-body">
                    <h6 class="text-sm text-uppercase text-secondary mb-3">
                      商品類型
                    </h6>
                    <p class="text-sm mb-2">
                      <strong>類型：</strong
                      >{{ formatProductType(product.product_type) }}
                    </p>
                    <p class="text-sm mb-2" v-if="product.product_type_detail">
                      <strong>類型詳情：</strong
                      >{{ product.product_type_detail }}
                    </p>
                  </div>
                </div>

                <!-- Golf Club Specs -->
                <div class="card mb-3">
                  <div class="card-body">
                    <h6 class="text-sm text-uppercase text-secondary mb-3">
                      球桿規格
                    </h6>
                    <div class="row">
                      <div class="col-md-6">
                        <p class="text-sm mb-2" v-if="product.hand">
                          <strong>慣用手：</strong
                          >{{ product.hand === "left" ? "左手" : "右手" }}
                        </p>
                        <p class="text-sm mb-2" v-if="product.skill_level">
                          <strong>技術水平：</strong
                          >{{ formatSkillLevel(product.skill_level) }}
                        </p>
                        <p class="text-sm mb-2" v-if="product.gender">
                          <strong>性別：</strong
                          >{{ product.gender === "male" ? "男性" : "女性" }}
                        </p>
                        <p class="text-sm mb-2" v-if="product.loft_angle">
                          <strong>仰角：</strong>{{ product.loft_angle }}°
                        </p>
                      </div>
                      <div class="col-md-6">
                        <p class="text-sm mb-2" v-if="product.length">
                          <strong>長度：</strong>{{ product.length }}
                        </p>
                        <p
                          class="text-sm mb-2"
                          v-if="product.length_adjustment"
                        >
                          <strong>長度調整：</strong
                          >{{ product.length_adjustment > 0 ? "+" : ""
                          }}{{ product.length_adjustment }} 英吋
                        </p>
                        <p class="text-sm mb-2" v-if="product.weight">
                          <strong>重量：</strong>{{ product.weight }} 克
                        </p>
                        <p class="text-sm mb-2" v-if="product.weight_type">
                          <strong>重量類型：</strong
                          >{{
                            product.weight_type === "single"
                              ? "單一重量"
                              : "混合重量"
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Shaft Specs -->
                <div
                  class="card mb-3"
                  v-if="
                    product.shaft_brand ||
                    product.shaft_weight ||
                    product.shaft_flex
                  "
                >
                  <div class="card-body">
                    <h6 class="text-sm text-uppercase text-secondary mb-3">
                      桿身規格
                    </h6>
                    <div class="row">
                      <div class="col-md-4">
                        <p class="text-sm mb-2" v-if="product.shaft_brand">
                          <strong>桿身品牌：</strong>{{ product.shaft_brand }}
                        </p>
                      </div>
                      <div class="col-md-4">
                        <p class="text-sm mb-2" v-if="product.shaft_weight">
                          <strong>桿身重量：</strong>{{ product.shaft_weight }}
                        </p>
                      </div>
                      <div class="col-md-4">
                        <p class="text-sm mb-2" v-if="product.shaft_flex">
                          <strong>桿身硬度：</strong>{{ product.shaft_flex }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Status Info -->
                <div class="card mb-3">
                  <div class="card-body">
                    <h6 class="text-sm text-uppercase text-secondary mb-3">
                      狀態資訊
                    </h6>
                    <div class="row">
                      <div class="col-md-6">
                        <p class="text-sm mb-2">
                          <strong>狀態：</strong>
                          <soft-badge
                            :color="getStatusColor(product.status)"
                            variant="gradient"
                            size="sm"
                          >
                            {{ formatStatus(product.status) }}
                          </soft-badge>
                        </p>
                        <p class="text-sm mb-2">
                          <strong>違規：</strong>
                          <span
                            v-if="product.is_violation"
                            class="badge badge-sm bg-gradient-danger"
                            >是</span
                          >
                          <span v-else class="text-success">否</span>
                        </p>
                        <p class="text-sm mb-2">
                          <strong>客製化：</strong
                          >{{ product.is_customized ? "是" : "否" }}
                        </p>
                      </div>
                      <div class="col-md-6">
                        <p class="text-sm mb-2">
                          <strong>實體庫存：</strong
                          >{{ product.physical_stock || 0 }}
                        </p>
                        <p class="text-sm mb-2">
                          <strong>入庫狀態：</strong
                          >{{ product.in_warehouse ? "已入庫" : "未入庫" }}
                        </p>
                        <p class="text-sm mb-2" v-if="product.is_certified">
                          <strong>認證：</strong>
                          <span class="badge badge-sm bg-gradient-success"
                            >已認證</span
                          >
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Description -->
                <div class="card mb-3" v-if="product.description">
                  <div class="card-body">
                    <h6 class="text-sm text-uppercase text-secondary mb-3">
                      商品描述
                    </h6>
                    <p class="text-sm">{{ product.description }}</p>
                  </div>
                </div>

                <hr class="horizontal dark" />

                <!-- Seller Info -->
                <div class="mb-3" v-if="product.seller">
                  <h6 class="text-sm">賣家資訊</h6>
                  <p class="text-sm mb-1">
                    <strong>姓名：</strong>{{ product.seller.name }}
                  </p>
                  <p class="text-sm mb-1">
                    <strong>電子郵件：</strong>{{ product.seller.email }}
                  </p>
                  <p class="text-sm mb-1" v-if="product.seller.phone">
                    <strong>電話：</strong>{{ product.seller.phone }}
                  </p>
                </div>

                <hr class="horizontal dark" />

                <!-- Additional Info -->
                <div class="row">
                  <div class="col-md-6">
                    <p class="text-xs text-secondary mb-1">
                      <strong>建立時間：</strong
                      >{{ formatDate(product.created_at) }}
                    </p>
                    <p class="text-xs text-secondary mb-1">
                      <strong>更新時間：</strong
                      >{{ formatDate(product.updated_at) }}
                    </p>
                  </div>
                  <div class="col-md-6">
                    <p class="text-sm mb-1">
                      <strong>特殊調整：</strong>
                      <span
                        :class="
                          product.special_adjustment !== 0
                            ? 'text-danger font-weight-bold'
                            : 'text-secondary'
                        "
                      >
                        {{ product.special_adjustment || 0 }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Variants Section -->
        <div class="card mt-4" v-if="variants.length > 0">
          <div class="card-header pb-0">
            <h6>商品變體</h6>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      變體編號
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      規格
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      價格
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      庫存
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      實體庫存
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      狀態
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="variant in variants" :key="variant.id">
                    <td>
                      <span class="text-xs font-weight-bold">{{
                        variant.variant_code
                      }}</span>
                    </td>
                    <td>
                      <span class="text-xs">{{
                        variant.display_name || formatVariantSpecs(variant)
                      }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold"
                        >NT$ {{ variant.price }}</span
                      >
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs">{{ variant.stock }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs">{{
                        variant.physical_stock || 0
                      }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <soft-badge
                        :color="
                          variant.status === 'active' ? 'success' : 'secondary'
                        "
                        variant="gradient"
                        size="sm"
                      >
                        {{ variant.status === "active" ? "上架" : "下架" }}
                      </soft-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- QR Code Modal -->
    <div
      v-if="showQRModal"
      class="modal fade show d-block"
      style="background: rgba(0, 0, 0, 0.5)"
      @click.self="showQRModal = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">商品 QR Code</h5>
            <button
              type="button"
              class="btn-close"
              @click="showQRModal = false"
            ></button>
          </div>
          <div class="modal-body text-center">
            <p class="mb-3">
              掃描此代碼以快速打開庫存管理頁面：
              <strong>{{ product.name }}</strong>
            </p>
            <div class="d-flex justify-content-center mb-3">
              <img :src="qrCodeUrl" alt="Product QR Code" class="img-fluid" />
            </div>
            <p class="text-muted text-xs">
              商品編號: {{ product.product_code }}
            </p>
            <p class="text-muted text-xs">掃描後將打開: {{ inventoryUrl }}</p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showQRModal = false"
            >
              關閉
            </button>
            <button type="button" class="btn btn-primary" @click="printQR">
              <i class="fas fa-print me-1"></i> 列印
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";
import SoftButton from "@/components/SoftButton.vue";
import request from "@/utils/request";

export default {
  name: "ProductDetail",
  components: {
    SoftBadge,
    SoftButton,
  },
  data() {
    return {
      product: {
        seller: {},
        brand: {},
      },
      variants: [],
      showQRModal: false,
    };
  },
  async created() {
    await this.fetchProduct();
    await this.fetchVariants();
  },
  computed: {
    inventoryUrl() {
      // Generate the full URL to the inventory management page for this product
      const baseUrl = window.location.origin;
      return `${baseUrl}/products/${this.product.id}/inventory`;
    },
    qrCodeUrl() {
      // Generate QR code with the inventory URL
      return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(
        this.inventoryUrl
      )}`;
    },
  },
  methods: {
    async fetchProduct() {
      try {
        const id = this.$route.params.id;
        const response = await request.get(`/api/admin/products/${id}`);
        this.product = response.data.data;
      } catch (error) {
        console.error("Failed to fetch product", error);
        this.$toast.error("載入商品詳情失敗。");
        this.$router.push("/products");
      }
    },
    async fetchVariants() {
      try {
        const id = this.$route.params.id;
        const response = await request.get(
          `/api/admin/products/${id}/variants`
        );
        this.variants = response.data.data;
      } catch (error) {
        console.error("Failed to fetch variants", error);
      }
    },
    formatVariantSpecs(variant) {
      const parts = [];
      if (variant.shaft_flex) parts.push(variant.shaft_flex);
      if (variant.loft_angle) parts.push(variant.loft_angle + "°");
      if (variant.length) parts.push(variant.length);
      if (variant.hand) parts.push(variant.hand === "left" ? "左手" : "右手");
      return parts.join(" / ") || "無規格";
    },
    getStatusColor(status) {
      switch (status) {
        case "active":
          return "success";
        case "inactive":
          return "secondary";
        case "sold":
          return "info";
        default:
          return "secondary";
      }
    },
    formatStatus(status) {
      if (!status) return "";
      const statusMap = {
        active: "上架",
        inactive: "下架",
        sold: "已售出",
      };
      return statusMap[status] || status;
    },
    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleString("zh-TW");
    },
    formatProductType(type) {
      const typeMap = {
        driver: "一號木桿 Driver",
        "1_wood": "一號木桿 1W",
        mini_driver: "小雞腿 Mini Driver",
        fairway_wood: "球道木桿",
        "3_wood": "三號木桿 3W",
        "4_wood": "四號木桿 4W",
        "5_wood": "五號木桿 5W",
        "7_wood": "七號木桿 7W",
        fairway_other: "球道木桿（其他）",
        iron_set: "鐵桿組",
        single_iron: "單支鐵桿",
        hybrid: "混合桿",
        "2_hybrid": "二號混合桿 2H",
        "3_hybrid": "三號混合桿 3H",
        "4_hybrid": "四號混合桿 4H",
        "5_hybrid": "五號混合桿 5H",
        hybrid_other: "混合桿（其他）",
        wedge: "挖起桿 Wedge",
        putter: "推桿",
        putter_blade: "推桿（刀背式）",
        putter_mallet: "推桿（槌頭式）",
        putter_arm_lock: "推桿（臂鎖式）",
        putter_other: "推桿（其他）",
        other: "其他",
      };
      return typeMap[type] || type;
    },
    formatSkillLevel(level) {
      const levelMap = {
        high: "高",
        medium: "中",
        low: "低",
      };
      return levelMap[level] || level;
    },
    printQR() {
      window.print();
    },
  },
};
</script>
